import { useEffect, useState } from 'react';
import { repeat, tap } from 'rxjs';
import { format } from 'date-fns';
import {
  responseHandler,
  IApiError,
} from '../../../../../../shared/responseHandler/responseHandler';
import { localeFormatterHelper } from '../../../../../../shared/helpers/formatter/localeFormatter.helper';
import {
  WindowsGeneratedReportPreviewRes,
  IArticlesReportParams,
  IOfferReportParams,
  IOrderReportParams,
  IInvoiceReportParams,
  IDebtReminderReportParams,
  IGetR10NotifierFileRes,
  reportsService,
  REPORTS_TASK_STATUS_POLLING_TIMEOUT_ERROR,
} from '../../../../../../shared/services/reports/reports.service';
import { C_Report, C_Win_Report_Task_Status } from '../../../../../../graphql/generatedModel';
import { IGeneratedReportData } from './components/reportPreviewArea';

const reportPollingErrorHandler = (err: IApiError) => {
  if (err.message === REPORTS_TASK_STATUS_POLLING_TIMEOUT_ERROR) {
    return {
      message: 'production.request_error_message',
      reportToSentry: true,
    };
  }
};

const createReportResponseHandler = <T>() =>
  responseHandler<T>({
    errorReturnType: {} as unknown as T,
    customErrorHandler: reportPollingErrorHandler,
  });

export const useReportPreview = (): IUseReportPreview => {
  const [loading, setLoading] = useState<boolean>(false);
  const [generatedReport, setGeneratedReport] = useState<IUseReportPreview['generatedReport']>({});

  useEffect(() => {
    const unsubArticlePricelistReport = reportsService.sub
      .createArticlesReport()
      .pipe(
        createReportResponseHandler<WindowsGeneratedReportPreviewRes>(),
        tap(() => setLoading(false)),
        repeat(),
      )
      .subscribe(setGeneratedReport);

    const unsubOfferReport = reportsService.sub
      .createOfferReport()
      .pipe(
        createReportResponseHandler<WindowsGeneratedReportPreviewRes>(),
        tap(() => setLoading(false)),
        repeat(),
      )
      .subscribe(setGeneratedReport);

    const unsubOrderReport = reportsService.sub
      .createOrderReport()
      .pipe(
        createReportResponseHandler<WindowsGeneratedReportPreviewRes>(),
        tap(() => setLoading(false)),
        repeat(),
      )
      .subscribe(setGeneratedReport);

    const unsubInvoiceReport = reportsService.sub
      .createInvoiceReport()
      .pipe(
        createReportResponseHandler<WindowsGeneratedReportPreviewRes>(),
        tap(() => setLoading(false)),
        repeat(),
      )
      .subscribe(setGeneratedReport);

    const unsubDebtReminderReport = reportsService.sub
      .createDebtReminderReport()
      .pipe(
        createReportResponseHandler<IGetR10NotifierFileRes>(),
        tap(() => setLoading(false)),
        repeat(),
      )
      .subscribe((v) => {
        // workaround for now until there isn't status in result
        setGeneratedReport({
          taskId: v.taskId,
          s3Key: v.s3Key ?? '',
          url: v.url ?? '',
          status: C_Win_Report_Task_Status.WRTS2_READY,
        });
      });

    return () => {
      unsubArticlePricelistReport.unsubscribe();
      unsubOfferReport.unsubscribe();
      unsubOrderReport.unsubscribe();
      unsubInvoiceReport.unsubscribe();
      unsubDebtReminderReport.unsubscribe();
    };
  }, []);

  const resetGeneratedReport = () => {
    setGeneratedReport({});
  };

  const generateArticlePricelist: IUseReportPreview['generateArticlePricelist'] = ({
    articlesIds,
    priceValidOn,
    customerId,
  }) => {
    const values: IArticlesReportParams = {
      articlesIds,
      priceValidOn,
      reportId: C_Report.R6_ARTICLE_LIST,
      priceMode: 'byCustomer',
      customerId,
    };
    setLoading(true);
    reportsService.pub.createArticlesReport(values);
  };

  const generateOffer: IUseReportPreview['generateOffer'] = ({ reportId, offerId }) => {
    const values: IOfferReportParams = {
      reportId,
      offerId,
    };
    setLoading(true);
    reportsService.pub.createOfferReport(values);
  };

  const generateDeliveryNote: IUseReportPreview['generateDeliveryNote'] = ({
    reportId,
    orderId,
  }) => {
    const values: IOrderReportParams = {
      reportId,
      orderId,
    };
    setLoading(true);
    reportsService.pub.createOrderReport(values);
  };

  const generateInvoice: IUseReportPreview['generateInvoice'] = ({ invoiceId }) => {
    const values: IInvoiceReportParams = {
      reportId: C_Report.R9_INVOICE_A4,
      invoiceId,
    };
    setLoading(true);
    reportsService.pub.createInvoiceReport(values);
  };

  const generatePaymentSlip: IUseReportPreview['generatePaymentSlip'] = ({ invoiceId }) => {
    const values: IInvoiceReportParams = {
      reportId: C_Report.R59_INVOICE_PAYMENTSLIP_WITH_QR,
      invoiceId,
    };
    setLoading(true);
    reportsService.pub.createInvoiceReport(values);
  };

  const generateDebtReminder: IUseReportPreview['generateDebtReminder'] = ({
    invoiceId,
    invoiceDate,
    customerId,
  }) => {
    const dateForReport = new Date(invoiceDate);
    const considerPaymentsTill = new Date(invoiceDate);
    considerPaymentsTill.setDate(considerPaymentsTill.getDate() - 1);
    const values = {
      invoicesList: [invoiceId],
      customerId,
      periodStart: format(localeFormatterHelper.localizedDate(dateForReport), 'yyyy-MM-dd'),
      periodEnd: format(localeFormatterHelper.localizedDate(dateForReport), 'yyyy-MM-dd'),
      paymentsTill: format(localeFormatterHelper.localizedDate(considerPaymentsTill), 'yyyy-MM-dd'),
    };
    setLoading(true);
    reportsService.pub.createDebtReminderReport({ params: values });
  };

  return {
    loading,
    generatedReport,
    resetGeneratedReport,
    generateArticlePricelist,
    generateOffer,
    generateDeliveryNote,
    generateInvoice,
    generatePaymentSlip,
    generateDebtReminder,
  };
};

interface IUseReportPreview {
  loading: boolean;
  generatedReport: IGeneratedReportData;
  resetGeneratedReport: () => void;
  generateArticlePricelist: (params: IArticlePricelistParams) => void;
  generateOffer: (params: IOfferReportParams) => void;
  generateDeliveryNote: (params: IOrderReportParams) => void;
  generateInvoice: (params: IInvoiceA4Params) => void;
  generatePaymentSlip: (params: IPaymentSlipParams) => void;
  generateDebtReminder: (params: IDebtReminderParams) => void;
}

type IArticlePricelistParams = Omit<
  IArticlesReportParams,
  'reportId' | 'priceMode' | 'priceCategory'
>;

type IInvoiceA4Params = Omit<IInvoiceReportParams, 'reportId'>;
type IPaymentSlipParams = Omit<IInvoiceReportParams, 'reportId'>;

type IDebtReminderParams = Omit<IDebtReminderReportParams, 'reportId'>;
